// arabicCybersecurity.ts
// Mission 115 - في قلب الحدث - مستقبل الأمن السيبراني
// Plateau TV arabe : analyse d'une attaque ransomware et stratégies de défense

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
} from './storyLoader';

export interface ArabicCybersecurityScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const arabicCyberCharacters: Character[] = [
  { id: 'leila',  name: 'ليلى الحسيني',   role: 'مقدمة البرنامج',           color: '#B45309', avatar: 'arabic_tv_leila_woman_30s' },
  { id: 'karim',  name: 'كريم المنصوري',  role: 'خبير الأمن السيبراني',     color: '#1E3A5F', avatar: 'arabic_tv_karim_man_40s'   },
  { id: 'omar',   name: 'عمر البكري',      role: 'خبير الاستجابة للحوادث',   color: '#065F46', avatar: 'arabic_tv_omar_man_30s'    },
];

// ─── Scène 1 ──────────────────────────────────────────────────────────────────

const scene1: SequenceItem = {
  type: 'story',
  id: 'ac-scene-1',
  title: 'الحلقة الأولى : هجوم الفدية الذي هزّ المنطقة',
  duration: 5,
  content: {
    narrativeText:
      "استوديو «في قلب الحدث» — أجواء متوترة. الشاشات تعرض خبراً عاجلاً: «مستشفى كبير يقع ضحية هجوم فدية يشلّ عملياته».»\n\n" +
      "**ليلى :** «خبر عاجل يسيطر على المشهد السيبراني : مستشفى رائد في المنطقة أُغلقت أنظمته بالكامل بسبب هجوم Ransomware. ضيفنا الخبير في الأمن السيبراني يشرح ما جرى.»\n\n" +
      "**اللاعب :** «ما حدث هو هجوم من نوع Ransomware-as-a-Service — خدمة جاهزة يشتريها مجرمون إلكترونيون ويطلقونها بضغطة زر. المستشفى دفع فدية بقيمة 4 ملايين دولار.»\n\n" +
      "**كريم :** «هل كان يمكن منع هذا الهجوم؟»\n\n" +
      "**اللاعب :** «نعم، بنسبة كبيرة. التحقيق أظهر ثلاثة إخفاقات : إيميل تصيّد (phishing) فتحه موظف، أنظمة قديمة غير مُحدَّثة منذ سنتين، وغياب تجزئة الشبكة (network segmentation).»\n\n" +
      "**عمر :** «ما الدرس الأكبر لمؤسسات المنطقة العربية؟»\n\n" +
      "**اللاعب :** «الأمن السيبراني ليس مشكلة IT — هو مشكلة أعمال. CEO يجب أن يسأل: ما هي قيمة البيانات التي أحميها؟ ما تكلفة التوقف عن العمل 48 ساعة؟ الإجابة تُحدد ميزانية الأمن.»\n\n" +
      "**ليلى :** «ما الخطوات الفورية التي يجب على أي مؤسسة اتخاذها الآن؟»\n\n" +
      "**اللاعب :** «أولاً، النسخ الاحتياطي المعزول (offline backup) — هذا وحده كان سيُنقذ المستشفى. ثانياً، تدريب الموظفين على التصيّد الاحتيالي. ثالثاً، تطبيق MFA على جميع الحسابات.»\n\n" +
      "**كريم :** «هل الذكاء الاصطناعي يُضاف الآن كسلاح هجومي في يد المهاجمين؟»\n\n" +
      "**اللاعب :** «بكل أسف، نعم. نرى الآن هجمات phishing تُكتب بذكاء اصطناعي بلغات مثالية وسياق شخصي — تصعب اكتشافها جداً. لذا التوعية البشرية لا تكفي وحدها، نحتاج طبقات تقنية متعددة.»",
    characters: ['leila', 'karim', 'omar'],
  } as StoryContent,
};

// ─── Card ─────────────────────────────────────────────────────────────────────

const card1: SequenceItem = {
  type: 'card',
  id: 'ac-card-1',
  title: 'إطار عمل الأمن السيبراني',
  duration: 5,
  content: {
    title: 'إطار NIST للأمن السيبراني : الوظائف الخمس',
    body:
      "## إطار عمل الأمن السيبراني (NIST CSF)\n\n" +
      "الإطار الأمريكي الوطني للأمن السيبراني (NIST CSF) يُنظّم الأمن في خمس وظائف متكاملة.\n\n" +
      "### ١. التعرّف (Identify)\n" +
      "- رسم خريطة الأصول الرقمية والمخاطر\n" +
      "- تصنيف البيانات حسب الحساسية\n" +
      "- تحديد التبعيات التقنية\n\n" +
      "### ٢. الحماية (Protect)\n" +
      "- تطبيق Zero Trust وMFA\n" +
      "- تحديث الأنظمة وإدارة التصحيحات (Patch Management)\n" +
      "- تشفير البيانات في التخزين والنقل\n" +
      "- تدريب الموظفين على الوعي الأمني\n\n" +
      "### ٣. الكشف (Detect)\n" +
      "- أنظمة رصد الأحداث (SIEM)\n" +
      "- تحليل السلوك الشاذ (Anomaly Detection)\n" +
      "- مراقبة 24/7 عبر SOC\n\n" +
      "### ٤. الاستجابة (Respond)\n" +
      "- خطة الاستجابة للحوادث (IR Plan)\n" +
      "- فرق الاستجابة السريعة (CSIRT)\n" +
      "- العزل الفوري للأنظمة المصابة\n\n" +
      "### ٥. التعافي (Recover)\n" +
      "- النسخ الاحتياطي المعزول (Offline Backup)\n" +
      "- اختبار الاسترداد الدوري (DR Drills)\n" +
      "- إعادة بناء الأنظمة بشكل آمن\n\n" +
      "### أنواع هجمات Ransomware\n" +
      "| النوع | الوصف | الحماية |\n" +
      "|---|---|---|\n" +
      "| Locker Ransomware | يُقفل الجهاز كلياً | Backup معزول |\n" +
      "| Crypto Ransomware | يُشفّر الملفات | Backup + تشفير مسبق |\n" +
      "| RaaS | خدمة جاهزة للمجرمين | SIEM + EDR |\n" +
      "| Double Extortion | تشفير + تهديد بالنشر | Zero Trust + DLP |",
  } as CardContent,
};

// ─── Chat ─────────────────────────────────────────────────────────────────────

const chat1: SequenceItem = {
  type: 'chat',
  id: 'ac-chat-1',
  title: 'نقاش : استراتيجيات الدفاع السيبراني',
  duration: 6,
  content: {
    tutorCharacterId: 'karim',
    messages: [
      {
        id: 'ac-chat-q1',
        sender: 'character',
        characterId: 'karim',
        text: 'ما أفضل إجراء وقائي أول لحماية مؤسسة من هجمات Ransomware؟',
        type: 'single-choice',
        options: [
          'شراء برنامج مضاد للفيروسات أغلى ثمناً',
          'فصل الإنترنت عن جميع الأجهزة',
          'تطبيق نسخ احتياطي معزول (Offline/Immutable Backup) مع اختبار الاسترداد دورياً',
          'تغيير كلمات المرور كل أسبوع',
        ],
        correctAnswer: 'تطبيق نسخ احتياطي معزول (Offline/Immutable Backup) مع اختبار الاسترداد دورياً',
        explanation: 'النسخ الاحتياطي المعزول (Air-gapped / Immutable Backup) هو «الشبكة الأمان» الأخيرة. إذا نجح هجوم الفدية، يمكنك الاسترداد دون دفع فدية. 80% من الضحايا الذين دفعوا الفدية لم يحصلوا على بياناتهم كاملة.',
      },
      {
        id: 'ac-chat-q2',
        sender: 'character',
        characterId: 'karim',
        text: 'ما المقصود بـ «تجزئة الشبكة» (Network Segmentation) ولماذا هي مهمة؟',
        type: 'single-choice',
        options: [
          'تقسيم الشبكة إلى مناطق معزولة يحدّ من انتشار الهجمات الأفقية',
          'تسريع اتصال الشبكة بتخصيص نطاق ترددي أعلى',
          'استخدام شبكات WiFi منفصلة للموظفين والزوار',
          'تشفير جميع البيانات المنقولة عبر الشبكة',
        ],
        correctAnswer: 'تقسيم الشبكة إلى مناطق معزولة يحدّ من انتشار الهجمات الأفقية',
        explanation: 'تجزئة الشبكة تمنع «الحركة الأفقية» (Lateral Movement) للمهاجم. في هجوم المستشفى، الإصابة بدأت بجهاز موظف وانتشرت لأنظمة حيوية لأن الشبكة لم تكن مُجزّأة. مع التجزئة، تحصر الضرر في منطقة محدودة.',
      },
      {
        id: 'ac-chat-q3',
        sender: 'character',
        characterId: 'karim',
        text: 'ما الفرق بين SIEM وSOC في منظومة الأمن السيبراني؟',
        type: 'single-choice',
        options: [
          'SIEM هو نظام برمجي لجمع وتحليل سجلات الأحداث، بينما SOC هو الفريق البشري الذي يُراقب ويستجيب',
          'كلاهما نفس الشيء، مجرد مصطلحين مختلفين',
          'SOC هو برنامج والSIEM فريق بشري',
          'SIEM للشركات الكبيرة فقط وSOC للصغيرة',
        ],
        correctAnswer: 'SIEM هو نظام برمجي لجمع وتحليل سجلات الأحداث، بينما SOC هو الفريق البشري الذي يُراقب ويستجيب',
        explanation: 'SIEM (Security Information and Event Management) = الأداة التقنية التي تجمع وتحلل ملايين الأحداث الأمنية. SOC (Security Operations Center) = الفريق البشري المتخصص الذي يتعامل مع التنبيهات، يحقق في الحوادث، ويتخذ القرارات. الاثنان متكاملان.',
      },
    ],
  } as ChatContent,
};

// ─── Quiz ─────────────────────────────────────────────────────────────────────

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'ac-quiz-1',
  title: 'اختبار : الأمن السيبراني',
  duration: 5,
  content: {
    questions: [
      {
        id: 'ac-q1-1',
        question: 'وفق إطار NIST CSF، ما الوظيفة التي تشمل «اختبار خطة الاسترداد من الكوارث»؟',
        type: 'single-choice',
        options: ['الحماية (Protect)', 'الكشف (Detect)', 'الاستجابة (Respond)', 'التعافي (Recover)'],
        correctAnswer: 'التعافي (Recover)',
        explanation: 'وظيفة «التعافي» في NIST CSF تشمل تخطيط الاسترداد، اختبار خطط DR (Disaster Recovery) بانتظام، والتحسين المستمر بعد كل حادثة. الاختبار الدوري هو ما يُثبت أن الخطة قابلة للتنفيذ فعلاً.',
      },
      {
        id: 'ac-q1-2',
        question: 'ما المقصود بـ Ransomware-as-a-Service (RaaS)؟',
        type: 'single-choice',
        options: [
          'خدمة سحابية مشروعة لحماية الملفات',
          'نموذج إجرامي يُتيح لغير المتخصصين شراء أدوات هجوم جاهزة مقابل حصة من الفدية',
          'خدمة استرداد الملفات المشفرة',
          'برنامج حكومي لمكافحة الفدية الرقمية',
        ],
        correctAnswer: 'نموذج إجرامي يُتيح لغير المتخصصين شراء أدوات هجوم جاهزة مقابل حصة من الفدية',
        explanation: 'RaaS حوّل الجريمة الإلكترونية لنموذج أعمال. المطورون يُنتجون الأداة، المشغلون يُطلقونها، والعائد مُقسَّم. هذا ضاعف عدد الهجمات لأن المهاجم لا يحتاج مهارة تقنية عالية.',
      },
      {
        id: 'ac-q1-3',
        question: 'ما مبدأ «المقاومة الافتراضية» (Zero Trust) باختصار؟',
        type: 'single-choice',
        options: [
          'عدم الثقة بأي مستخدم أو جهاز داخل الشبكة أو خارجها دون التحقق المستمر',
          'قطع الإنترنت عن الشبكة الداخلية كلياً',
          'الثقة بالموظفين الداخليين والتحقق من الخارجيين فقط',
          'استخدام كلمات مرور طويلة لجميع الأنظمة',
        ],
        correctAnswer: 'عدم الثقة بأي مستخدم أو جهاز داخل الشبكة أو خارجها دون التحقق المستمر',
        explanation: 'Zero Trust يقوم على مبدأ «لا تثق أبداً، تحقق دائماً» (Never Trust, Always Verify). حتى الموظف الداخلي يجب التحقق من هويته وصلاحياته في كل وصول. هذا يمنع الحركة الأفقية حتى بعد اختراق حساب واحد.',
      },
    ],
  } as QuizContent,
};

// ─── Chapitre ─────────────────────────────────────────────────────────────────

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'الأمن السيبراني',
  subtitle: 'Arabic — تهديدات الأمن السيبراني واستراتيجيات الدفاع',
  learningObjectives: [
    'فهم إطار NIST للأمن السيبراني',
    'تحليل هجمات Ransomware وآليات انتشارها',
    'التمييز بين أدوات وفرق الأمن السيبراني',
    'بناء حجة استراتيجية للاستثمار في الأمن بالعربية',
  ],
  sequence: [scene1, card1, chat1, quiz1],
};

export const arabicCybersecurityScenario: ArabicCybersecurityScenario = {
  id: 'arabic-cybersecurity',
  title: 'في قلب الحدث - مستقبل الأمن السيبراني',
  subtitle: 'Cybersécurité : défis croissants',
  certification: 'Arabic',
  characters: arabicCyberCharacters,
  chapters: [chapter1],
};

export default arabicCybersecurityScenario;
